import {
  Component,
  ElementRef,
  Input,
  Optional,
  Self,
  ViewChild,
  OnDestroy,
  AfterViewInit,
  effect,
  inject,
  input,
  output,
  afterNextRender,
} from "@angular/core";
import { ControlValueAccessor, NgControl } from "@angular/forms";
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats } from "@angular/material/core";
import { MatCalendar, MatCalendarCellClassFunction } from "@angular/material/datepicker";
import { MatFormFieldControl } from "@angular/material/form-field";
import { FocusMonitor } from "@angular/cdk/a11y";
import { Subject } from "rxjs";
import moment from "moment-timezone";
import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";

@Component({
  selector: "app-custom-datepicker-tz",
  templateUrl: "./custom-datepicker-with-displayed-tz.component.html",
  styleUrl: "./custom-datepicker-with-displayed-tz.component.scss",
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: CustomDatepickerWithDisplayedTzComponent,
    },
  ],
})
export class CustomDatepickerWithDisplayedTzComponent<D>
  implements ControlValueAccessor, MatFormFieldControl<D>, OnDestroy, AfterViewInit
{
  /** MatFormFieldControl contract */
  static nextId = 0;
  stateChanges = new Subject<void>();
  focused = false;
  controlType = "custom-datepicker-tz";
  id = `custom-datepicker-tz-${CustomDatepickerWithDisplayedTzComponent.nextId++}`;
  describedBy = "";

  get empty() {
    return !this._value;
  }
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }
  get errorState() {
    return !!this.ngControl?.invalid && (this.ngControl.touched || this.ngControl.dirty);
  }

  /** Inputs */
  @Input() placeholder: string = "";

  @Input() dateFilter: (date: D) => boolean;

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }
  private _required = false;

  @Input() minDate: D | null = null;
  @Input() maxDate: D | null = null;
  @Input() startAt: D | null = null;
  @Input() readonly = false;
  @Input() closeOnSelected = false;
  @Input() dateClass: (d: D) => string | null = () => null;
  timezone = input<string>("UTC");

  displayAiracDates = input<boolean>(false);

  REFERENCE_AIRAC_DATE = moment("2018-11-08");

  airacDateClass: MatCalendarCellClassFunction<moment.Moment> = (cellDate, view) => {
    if (view === "month") {
      // Calculate the difference in days from the reference date
      const diffInDays = cellDate.startOf("day").diff(this.REFERENCE_AIRAC_DATE.startOf("day"), "days");
      // Check if the difference is divisible by 28
      return diffInDays >= 0 && diffInDays % 28 === 0 ? "airac-date-class" : "";
    }

    return "";
  };

  /** Outputs */
  blur = output<FocusEvent>();
  dateChange = output<D>();

  @ViewChild(MatCalendar) calendar?: MatCalendar<D>;
  @ViewChild("inputEl", { static: true }) inputEl!: ElementRef<HTMLInputElement>;

  /** Value with getter/setter so MatFormField picks up changes */
  private _value: D | null = null;
  get value(): D | null {
    return this._value;
  }
  set value(v: D | null) {
    this._value = v;
    this.stateChanges.next();
  }

  overlayOpen = false;
  disabled = false;

  private onChange: (value: D | null) => void = () => {};
  private onTouched: () => void = () => {};

  dateFormats: MatDateFormats = inject(MAT_DATE_FORMATS);

  private skipFirstEffect = true;

  constructor(
    private hostRef: ElementRef<HTMLElement>,
    private fm: FocusMonitor,
    @Optional() @Self() public ngControl: NgControl,
    private dateAdapter: DateAdapter<D>
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    // React to timezone changes
    effect(() => {
      const tz = this.timezone(); // read signal

      if (this.skipFirstEffect) {
        this.skipFirstEffect = false;
        return;
      }

      if (this.value) {
        this.value = this.adjustToTimezone(this.value, tz, true);
        this.emitValue(this.value);
      }
    });
  }

  ngAfterViewInit() {
    // Monitor the *inner input* for focus state
    this.fm.monitor(this.inputEl.nativeElement, true).subscribe((origin) => {
      this.focused = !!origin;
      if (!origin) {
        // When focus is lost, emit blur to the parent
        this.blur.emit(new FocusEvent("focusEvent"));
        this.ngControl.control?.markAsDirty();
        this.onTouched();
      }
      this.stateChanges.next();
    });
  }

  /** Display value inside the form field */
  get displayValue(): string {
    return this.value ? this.dateAdapter.format(this.value, this.dateFormats.display.dateInput) : "";
  }

  /** Utilities */
  private adjustToTimezone(date: D, tz: string, normalizeToStartOfDay = false): D {
    if (date) {
      const d = this.dateAdapter.deserialize(date) as D;
      const year = this.dateAdapter.getYear(d);
      const month = this.dateAdapter.getMonth(d);
      const day = this.dateAdapter.getDate(d);
      const zonedDate = normalizeToStartOfDay ? moment.tz({ year, month, day }, tz).startOf("day") : moment(d).tz(tz);
      console.log({ input: date, asDate: zonedDate, asISO: zonedDate.toISOString() });
      return this.dateAdapter.deserialize(zonedDate) as D;
    }
  }

  formatWithZone(val: D | null, tz: string = "UTC"): string {
    if (!val) return "-";
    const deser = this.dateAdapter.deserialize(val) as any;
    const asDate = moment.isMoment(deser) ? deser.toDate() : deser instanceof Date ? deser : new Date(deser);
    return moment(asDate).tz(tz).format("DD MMM YYYY HH:mm A z");
  }

  /** Calendar selection */
  onSelected(date: D) {
    this.value = date ? this.adjustToTimezone(date, this.timezone(), true) : null;

    this.emitValue(this.value);
    if (this.closeOnSelected) this.closeOverlay();
  }

  /** Overlay controls */
  openOverlay() {
    if (this.readonly || this.disabled) return;

    this.overlayOpen = true;

    setTimeout(() => {
      if (this.calendar && this.value) {
        this.calendar.activeDate = this.value;
        this.calendar.selected = this.value;
      }
    });
  }
  closeOverlay() {
    this.overlayOpen = false;
  }

  /** ControlValueAccessor */
  writeValue(obj: D | null): void {
    this.value = obj ? this.adjustToTimezone(obj, this.timezone(), false) : null;
  }
  registerOnChange(fn: (value: D | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.readonly = isDisabled;
    this.stateChanges.next();
  }
  private emitValue(value: D | null) {
    this.onChange(value);
    this.onTouched();
    this.dateChange.emit(value);
    this.stateChanges.next();
  }

  /** MatFormFieldControl */
  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(" ");
    if (this.inputEl) {
      this.inputEl.nativeElement.setAttribute("aria-describedby", this.describedBy);
    }
  }
  onContainerClick() {
    // Focus the inner input when the container is clicked
    this.inputEl?.nativeElement.focus();
    this.openOverlay();
  }

  /** Cleanup */
  ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.inputEl.nativeElement);
  }
}
